import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import databaseService from '../services/databaseService';
import { getSizeColor } from '../data/constants';
import { COLOR_FILTERS } from '../data/colors';
import MaterialIcon from './MaterialIcon';
import logger from '../utils/logger.js';
import './ActualSizeSticker.css';

const ActualSizeSticker = ({
  listingId,
  title = '',
  size = 'MICRO',
  color = 'Black (Matte)',
  width = 200,
  height = 120,
  className = '',
  style = {},
  onStickerClick = null,
  id = null,
  isAdminMode = false,
  showDimensions = true,
  showGrid = false,
  svgPath = null, // Prop to override database svgPath
}) => {
  // State for data loading
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Load data from database
  const loadData = useCallback(async () => {
    if (!listingId) {
      setError('No listing ID provided');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Get unified data (contains all necessary information)
      // Use cache-busting to ensure fresh data after dimension fixes
      const unifiedData = await databaseService.getEtsyListingData(listingId, true);

      if (!unifiedData) {
        setError('No data found for this listing');
        setLoading(false);
        return;
      }

      // Data is already combined in unified structure
      const combinedData = {
        ...unifiedData,
        // Ensure compatibility with existing code
        availableSizes: unifiedData.available_sizes || [],
        availableColors: unifiedData.available_colors || [],
        firstImageUrl: unifiedData.first_image_url,
        largestSizeImageUrl: unifiedData.largest_size_image_url,
      };

      /*logger.log('🔍 Combined data for listing', listingId, ':', {
        svgPath: combinedData.svgPath,
        category: combinedData.category,
        title: combinedData.title,
        hasInventory: !!combinedData.inventory,
        hasImages: !!combinedData.images,
        dimensions: combinedData.dimensions
      });*/

      setData(combinedData);
      setLoading(false);
    } catch (err) {
      console.error(`❌ [ActualSizeSticker] Error loading data for listing ${listingId}:`, err);
      setError('Failed to load listing data');
      setLoading(false);
    }
  }, [listingId]);

  // Load data on mount
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Extract dimensions from size string
  const extractDimensions = useCallback((sizeString) => {
    if (!sizeString) return null;

    // Pattern 1: "ATOM 0.8w x 0.4h inches" or "MICRO 4.4w x 2.1h inches" (T-Rex format)
    const pattern1 = /(\w+)\s+([\d.]+)w\s*x\s*([\d.]+)h\s*inches?/i;
    const match1 = sizeString.match(pattern1);

    if (match1) {
      const [, sizeName, widthInches, heightInches] = match1;
      return {
        sizeName: sizeName.toUpperCase(),
        widthInches: parseFloat(widthInches),
        heightInches: parseFloat(heightInches),
        unit: 'inches',
      };
    }

    // Pattern 2: "MICRO height 0.6 inches" or "SMALL height 1.2 inches" (Great Dane format)
    const pattern2 = /(\w+)\s+height\s+([\d.]+)\s*inches?/i;
    const match2 = sizeString.match(pattern2);

    if (match2) {
      const [, sizeName, heightInches] = match2;
      // For height-only stickers, assume square aspect ratio or use a default width
      const widthInches = parseFloat(heightInches) * 1.5; // Assume 1.5:1 aspect ratio

      // Normalize size names (MED -> MEDIUM)
      let normalizedSizeName = sizeName.toUpperCase();
      if (normalizedSizeName === 'MED') {
        normalizedSizeName = 'MEDIUM';
      }

      return {
        sizeName: normalizedSizeName,
        widthInches: widthInches,
        heightInches: parseFloat(heightInches),
        unit: 'inches',
      };
    }

    return null;
  }, []);

  // Get dimensions for current size
  const currentDimensions = useMemo(() => {
    // Use the loaded data from useEffect
    if (!data) return null;

    // First priority: Check stored dimensions from stickers table
    const dimensionsData = data?.data?.dimensions || data?.dimensions;

    if (dimensionsData) {
      try {
        const storedDimensions =
          typeof dimensionsData === 'string' ? JSON.parse(dimensionsData) : dimensionsData;

        const sizeData = storedDimensions[size.toUpperCase()];
        if (sizeData && sizeData.width && sizeData.height) {
          // logger.log(`✅ Using stored dimensions for ${listingId} ${size}:`, sizeData);
          return {
            sizeName: size.toUpperCase(),
            widthInches: sizeData.width,
            heightInches: sizeData.height,
            unit: 'inches',
          };
        }
      } catch (error) {
        logger.warn(`⚠️ Failed to parse stored dimensions for ${listingId}:`, error);
      }
    }

    // Second priority: Check inventory data (Etsy API)
    const inventoryData = data?.data?.inventory || data?.inventory;
    if (inventoryData?.products) {
      // Find the specific size in inventory
      for (const product of inventoryData.products) {
        for (const propValue of product.property_values || []) {
          if (propValue.property_name === 'Size' || propValue.property_name === 'Style') {
            for (const value of propValue.values || []) {
              const dimensions = extractDimensions(value);
              if (dimensions && dimensions.sizeName === size.toUpperCase()) {
                logger.log(`✅ Using inventory dimensions for ${listingId} ${size}:`, dimensions);
                return dimensions;
              }
            }
          }
        }
      }

      // Debug: Log available sizes for troubleshooting
      const availableSizes = [];
      data.inventory.products.forEach((product) => {
        product.property_values?.forEach((propValue) => {
          if (propValue.property_name === 'Size' || propValue.property_name === 'Style') {
            propValue.values?.forEach((value) => {
              const dimensions = extractDimensions(value);
              if (dimensions) {
                availableSizes.push(
                  `${dimensions.sizeName}: ${dimensions.widthInches}" × ${dimensions.heightInches}"`
                );
              }
            });
          }
        });
      });
      if (availableSizes.length > 0) {
        // logger.log(`🔍 Available inventory sizes for ${listingId}:`, availableSizes);
      }
    }

    // logger.warn(`⚠️ No dimensions found for ${listingId} size ${size}`);
    return null;
  }, [data, size, extractDimensions, listingId]);

  // Convert inches to pixels for display
  const getPixelDimensions = useCallback(
    (dimensions) => {
      if (!dimensions) {
        // Silently return fallback dimensions
        return { width: 10, height: 10 }; // Fallback dimensions
      }

      // Convert inches to pixels (assuming 96 DPI for web)
      const dpi = 96;
      const widthPixels = Math.round(dimensions.widthInches * dpi);
      const heightPixels = Math.round(dimensions.heightInches * dpi);

      // Using actual dimensions for rendering

      return { width: widthPixels, height: heightPixels };
    },
    [listingId, size]
  );

  // Get the appropriate image source
  const getImageSource = useCallback(() => {
    // First priority: svgPath prop (from parallax scene data)
    if (svgPath) {
      return {
        url: svgPath,
        type: 'clean',
        processing: null,
      };
    }

    // Second priority: svgPath from database (with path resolution)
    if (data?.svgPath) {
      let resolvedPath;
      // Handle different path formats
      if (data.svgPath.startsWith('/stickers/')) {
        // Convert old sticker paths to new asset paths
        const fileName = data.svgPath.split('/').pop();
        const category = data?.category?.toLowerCase() || 'dinosaurs';

        // Special case for T-Rex - use the actual filename that exists
        let finalFileName = fileName.replace('.svg', '.png');
        if (fileName === 't-rex.svg') {
          finalFileName = 'trex-2.png';
        }

        resolvedPath = `/assets/images/${category}/${finalFileName}`;
      } else if (data.svgPath.startsWith('/assets/')) {
        // Already in correct format
        resolvedPath = data.svgPath;
      } else {
        // Assume it's a relative path
        resolvedPath = data.svgPath;
      }
      return {
        url: resolvedPath,
        type: 'clean',
        processing: null,
      };
    }

    // Second priority: size-specific image from Etsy using existing mapping logic
    if (data?.images && data?.variationImages) {
      const images = data.images;
      const variationImages = data.variationImages;

      // Use the same logic as CoreStickerCard to find the correct image for the selected size
      if (variationImages.results && variationImages.results.length > 0) {
        // First try to find size-based variation
        const sizeMatch = size.match(/^(\w+)/);
        if (sizeMatch) {
          const sizeName = sizeMatch[1];
          const sizeVariation = variationImages.results.find((v) => {
            const vSizeMatch = v.value.match(/^(\w+)/);
            return vSizeMatch && vSizeMatch[1] === sizeName;
          });

          if (sizeVariation) {
            const image = images.find((img) => img.listing_image_id === sizeVariation.image_id);
            if (image) {
              return {
                url: image.url_fullxfull,
                type: 'etsy',
                processing: {
                  crop: 'auto',
                  zoom: 1.8,
                  contrast: 1.2,
                  brightness: 1.1,
                },
              };
            }
          }
        }
      }

      // Fallback to index-based correlation using actual available sizes
      const availableSizes = data?.availableSizes || [];
      const sizeIndex = availableSizes.indexOf(size.toUpperCase());
      if (sizeIndex >= 0 && images[sizeIndex + 1]) {
        return {
          url: images[sizeIndex + 1].url_fullxfull,
          type: 'etsy',
          processing: {
            crop: 'auto',
            zoom: 1.8,
            contrast: 1.2,
            brightness: 1.1,
          },
        };
      }
    }

    // Third priority: first image from Etsy
    if (data?.firstImageUrl) {
      return {
        url: data.firstImageUrl,
        type: 'etsy',
        processing: {
          crop: 'auto',
          zoom: 1.6,
          contrast: 1.2,
          brightness: 1.1,
        },
      };
    }

    // Final fallback: category-based image
    const category = data?.category?.toLowerCase() || 'dinosaurs';
    return {
      url: `/assets/images/${category}/trex-2.png`,
      type: 'fallback',
      processing: null,
    };
  }, [data, size, svgPath]);

  // Handle image loading
  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
  }, []);

  const handleImageError = useCallback(() => {
    setImageLoaded(false);
  }, []);

  // Handle sticker click
  const handleClick = useCallback(() => {
    if (onStickerClick) {
      const imageSource = getImageSource();
      onStickerClick({
        listingId,
        title: data?.title || title,
        size,
        color,
        dimensions: currentDimensions,
        imageSource: imageSource.url,
        imageType: imageSource.type,
      });
    }
  }, [onStickerClick, listingId, data, title, size, color, currentDimensions, getImageSource]);

  // Calculate display dimensions
  const displayDimensions = useMemo(() => {
    return getPixelDimensions(currentDimensions);
  }, [currentDimensions, getPixelDimensions]);

  // Loading state
  if (loading) {
    return (
      <div className="actual-size-sticker loading" style={style}>
        <div className="loading-placeholder">
          <img
            src="/assets/svgs/clothedEye-watermark.svg"
            alt="ClothedEye"
            className="loading-logo"
          />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="actual-size-sticker error" style={style}>
        <div className="error-content">
          <MaterialIcon icon="warning" size={16} className="error-icon" />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  const imageSource = getImageSource();
  const { width: pixelWidth, height: pixelHeight } = displayDimensions;

  return (
    <div
      className={`actual-size-sticker ${className}`}
      style={{
        ...style,
        width: pixelWidth,
        height: pixelHeight,
      }}
      onClick={handleClick}
    >
      {/* Grid overlay (optional) */}
      {showGrid && (
        <div className="sticker-grid">
          <div className="grid-lines" />
        </div>
      )}

      {/* Sticker image */}
      {imageSource && (
        <img
          src={imageSource.url}
          alt={data?.title || title}
          className="sticker-image"
          data-type={imageSource.type}
          style={{
            width: '100%',
            height: '100%',
            position: 'relative',
            zIndex: 2,
            borderRadius: '4px',
            opacity: 1, // Force visible
            filter:
              COLOR_FILTERS[color] ||
              COLOR_FILTERS['Black (Matte)'] ||
              'brightness(0) saturate(100%)',
          }}
          onLoad={handleImageLoad}
          onError={handleImageError}
        />
      )}

      {/* Image type indicator (for debugging) */}
      {imageSource && isAdminMode && (
        <div className={`image-type-indicator ${imageSource.type}`}>{imageSource.type}</div>
      )}

      {/* Fallback placeholder - only show if no image source */}
      {!imageSource && (
        <div className="sticker-placeholder">
          <img
            src="/assets/svgs/clothedEye-watermark.svg"
            alt="ClothedEye"
            className="placeholder-logo"
          />
        </div>
      )}

      {/* Dimensions overlay */}
      {showDimensions && currentDimensions && (
        <div className="dimensions-overlay">
          <span className="dimension-text">
            {currentDimensions.widthInches}" × {currentDimensions.heightInches}"
          </span>
        </div>
      )}
    </div>
  );
};

ActualSizeSticker.propTypes = {
  listingId: PropTypes.string.isRequired,
  title: PropTypes.string,
  size: PropTypes.string,
  color: PropTypes.string,
  width: PropTypes.number,
  height: PropTypes.number,
  className: PropTypes.string,
  style: PropTypes.object,
  onStickerClick: PropTypes.func,
  id: PropTypes.string,
  isAdminMode: PropTypes.bool,
  showDimensions: PropTypes.bool,
  showGrid: PropTypes.bool,
};

export default ActualSizeSticker;
